import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import satelliteMap from '@/assets/satellite-map.avif';

// SEO Meta
const pageTitle = "Local Web Design for Trades | SmartSite Solutions";
const pageDescription = "Specialist websites and local SEO for trades in Cardiff, Swansea, Newport, Bridgend, Neath Port Talbot and Porthcawl. Face-to-face consultations available.";
const coreRegions = [
  { 
    name: 'Cardiff', 
    slug: 'cardiff', 
    description: 'Web Design & SEO for the capital.',
  },
  { 
    name: 'Swansea', 
    slug: 'swansea', 
    description: 'Web Design & SEO for Swansea Bay.',
  },
  { 
    name: 'Newport', 
    slug: 'newport', 
    description: 'Web Design & SEO for Gwent.',
  },
  { 
    name: 'Bridgend', 
    slug: 'bridgend', 
    description: 'Web Design & SEO for Mid Glamorgan.',
  },
  { 
    name: 'Neath Port Talbot', 
    slug: 'neath', 
    description: 'Web Design & SEO for NPT.',
  },
  { 
    name: 'Porthcawl', 
    slug: 'porthcawl', 
    description: 'Web Design & SEO for the coast.',
  },
];

const generateCollectionPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'South Wales Service Areas | SmartSite Solutions',
  description: 'Professional web design and SEO services across 6 key regions in South Wales.',
  url: 'https://smartsite-lead-gen.lovable.app/locations',
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: coreRegions.map((region, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: `${region.name} Web Design & SEO`,
      url: `https://smartsite-lead-gen.lovable.app/locations/${region.slug}`,
    })),
  },
});

// Satellite Radius Map Section - Full width feature with floating glass card
const SatelliteRadiusSection = () => (
  <div className="w-full max-w-7xl mx-auto rounded-3xl overflow-hidden border border-slate-700/50 relative">
    {/* Satellite Map Image - Lazy loaded as it's below the fold */}
    <img 
      src={satelliteMap} 
      alt="Satellite view of South Wales showing 50-mile service radius around North Cornelly"
      loading="lazy"
      decoding="async"
      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1280px"
      className="w-full h-[650px] object-cover object-[center_100%]"
    />
    
    {/* Floating Glassmorphism Info Card */}
    <div className="absolute bottom-6 left-6 right-6 md:right-auto md:max-w-lg">
      <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8 shadow-2xl">
        <h2 className="font-display text-xl md:text-2xl font-bold mb-3 text-white">
          Face-to-Face Strategy Sessions.
        </h2>
        <p className="text-white/80 mb-4 leading-relaxed">
          Based in North Cornelly. We provide in-person consultations within this 50-mile radius.
        </p>
        <p className="text-sm text-white/50 italic">
          Further afield? We manage clients nationwide via Zoom.
        </p>
      </div>
    </div>
  </div>
);

export default function Locations() {
  useEffect(() => {
    const existingSchema = document.querySelector('script[data-schema="collection-page"]');
    if (existingSchema) existingSchema.remove();

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.setAttribute('data-schema', 'collection-page');
    schemaScript.textContent = JSON.stringify(generateCollectionPageSchema());
    document.head.appendChild(schemaScript);

    return () => {
      const schema = document.querySelector('script[data-schema="collection-page"]');
      if (schema) schema.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/locations" />
      </Helmet>
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
          <div className="absolute inset-0 mesh-gradient opacity-30" />
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Local{' '}
                <span className="text-gradient">Digital Engineering</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground mb-8">
                Based in North Cornelly. Dominate your local market across the M4 corridor.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Satellite Radius Section - Desktop Only */}
        <section className="hidden md:block py-12 lg:py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <SatelliteRadiusSection />
            </motion.div>
          </div>
        </section>

        {/* Regional Hubs Grid */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                Key Service Areas
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Specialist web design and SEO for trades across South Wales.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {coreRegions.map((region, index) => (
                <motion.div
                  key={region.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/locations/${region.slug}`}
                    className="glass-card p-5 md:p-6 hover:bg-white/10 transition-all block group h-full"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/20 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-orange-500" />
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-lg group-hover:text-orange-400 transition-colors">
                          {region.name} Trades
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {region.description}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center text-orange-400 text-sm font-medium group-hover:text-orange-300 transition-colors">
                      View Area
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-6 md:p-8 lg:p-12 text-center"
            >
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                Don't see your town?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
                We serve the whole South Wales region. Get in touch to discuss your local market.
              </p>
              
              <Link to="/contact#contact-form">
                <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all">
                  Check My Area
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
